import {
    Crud,
    HttpClient,
    httpClient,
    RequestOptions,
    QueryParameter,
    HttpClientResponseContent
} from 'lib/HttpClient';
import { ITagGroup, Location } from 'components/location/models/LocationModel';
import { ILocationExportOperationArgs } from '@pepperhq/types-sdk';
import { IPagedCollection } from 'lib/api/paging';
import { v4 as uuid } from 'uuid';
import { ILocationExportOperation } from './models/LocationOperationModel';

export interface LocationsQuery {
    state?: string;
    title?: string;
    limit?: number;
    startKey?: string;
    /** Default-hidden fields to opt back into the list projection, e.g. ['address', 'description']. */
    include?: string[];
}

function toQueryParameters(query: LocationsQuery = {}): QueryParameter[] {
    const queryParameters: QueryParameter[] = [];
    if (query.state) {
        queryParameters.push({ key: 'state', value: query.state });
    }
    if (query.title) {
        queryParameters.push({ key: 'title', value: query.title });
    }
    if (query.limit !== undefined) {
        queryParameters.push({ key: 'limit', value: query.limit });
    }
    if (query.startKey) {
        queryParameters.push({ key: 'startKey', value: query.startKey });
    }
    if (query.include?.length) {
        queryParameters.push({ key: 'include', value: query.include.join(',') });
    }
    return queryParameters;
}

class LocationApi extends Crud {
    constructor(client: HttpClient) {
        super(client, import.meta.env.API_SERVER_URL, 'locations', {
            'x-auth-id': import.meta.env.CONSOLE_FIREBASE_PROJECT_ID,
            'x-api-version': 13
        });
    }

    getTagGroups() {
        return this.httpClient.get(`${this.api}/locations/taggroups`);
    }

    deleteTagGroup(tagGroupId: string) {
        return this.httpClient.delete(`${this.api}/locations/taggroups/${tagGroupId}`, {
            skipResponseBody: true,
            skipStringify: true
        });
    }

    updateTagGroup(tagGroup: ITagGroup) {
        return this.httpClient.put(`${this.api}/locations/taggroups`, {
            body: tagGroup
        });
    }

    create(options: RequestOptions) {
        return this.httpClient.post(`${this.api}/locations`, {
            ...options
        });
    }
    exportLocations(filters?: ILocationExportOperationArgs, nonce?: string) {
        return this.httpClient.post(`${this.api}/locations/exports`, {
            headers: {
                'x-auth-id': import.meta.env.CONSOLE_FIREBASE_PROJECT_ID,
                'x-pepper-req-nonce': nonce || uuid()
            },
            body: { filters }
        });
    }

    getExportOperation(operationId: string): Promise<HttpClientResponseContent<ILocationExportOperation>> {
        return this.httpClient.get(`${this.api}/operations/${operationId}`);
    }

    async downloadExportFile(href: string): Promise<Response> {
        return fetch(`${this.api}${href}`, {
            headers: {
                'x-auth-id': import.meta.env.CONSOLE_FIREBASE_PROJECT_ID,
                Authorization: `Bearer ${this.httpClient.getToken()}`
            }
        });
    }

    getExportOperations(params?: {
        status?: 'PENDING' | 'DONE' | 'ERROR' | 'TIMEOUT';
        nextKey?: string;
    }): Promise<HttpClientResponseContent<IPagedCollection<ILocationExportOperation>>> {
        const queryParameters: QueryParameter[] = [
            { key: 'limit', value: '20' },
            { key: 'sort', value: '-createdAt' }
        ];
        if (params?.status) {
            queryParameters.push({ key: 'status', value: params.status });
        }
        if (params?.nextKey) {
            queryParameters.push({ key: 'startKey', value: params.nextKey });
        }
        return this.httpClient.get(`${this.api}/locations/exports/operations`, {
            queryParameters
        });
    }
    /** Fetch a single page of locations (v13 cursor pagination). */
    getLocationsPage(
        query: LocationsQuery = {}
    ): Promise<HttpClientResponseContent<IPagedCollection<Location>>> {
        return this.getList({ queryParameters: toQueryParameters(query) });
    }

    /** Walk `nextKey` cursor to exhaustion, accumulating every location. */
    async getAllLocations(query: LocationsQuery = {}): Promise<Location[]> {
        const allLocations: Location[] = [];
        let startKey: string | undefined;
        do {
            const response = await this.getLocationsPage({ ...query, startKey });
            if (!response.ok) {
                throw new Error(response.body?.message ?? 'Failed to get locations');
            }
            allLocations.push(...response.body.items);
            startKey = response.body.page?.nextKey;
        } while (startKey);
        return allLocations;
    }
}

export const locationApi = new LocationApi(httpClient);
